'use client';
import React, { useRef, useState } from 'react';

import styles from './Features.module.scss';
import Segmented from './Segmented/Segmented';
import CarModelCarousel from './CarModelCarousel/CarModelCarousel';

export default function Features({ carousel, buttonConfig }) {
  const swiperRef = useRef(null);
  const [selectedCarType, setSelectedCarType] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // 数据解析函数
  const getTitle = (item) => {
    if (item && item.fields) {
      return item.fields.title?.value || 'Feature Title';
    }
    return item.title || 'Feature Title';
  };

  const getImages = (item) => {
    if (item && item.fields && item.fields.images) {
      return item.fields.images.map((imageItem) => {
        if (imageItem.fields && imageItem.fields.image) {
          return {
            id: imageItem.id,
            image: imageItem.fields.image,
          };
        }
        return imageItem;
      });
    }
    return item.images || [];
  };

  const getDescription = (item) => {
    if (item && item.fields && item.fields.description) {
      return item.fields.description.map((descItem) => {
        if (descItem.fields) {
          return {
            title: descItem.fields.title?.value || '',
            text: descItem.fields.text?.value || '',
          };
        }
        return descItem;
      });
    }
    return item.description || [];
  };

  const getButtons = (item) => {
    if (item && item.fields && item.fields.buttons) {
      return item.fields.buttons.map((buttonItem) => {
        if (buttonItem.fields) {
          return {
            // buttonItem.fields.path?.value?.href || '#'
            label: buttonItem.fields.label?.value || '',
            path: `${buttonItem.fields.path?.value?.href || ''}${
              buttonItem.fields.path?.value?.querystring || ''
            }`,
            isComingSoon: buttonItem.fields.isComingSoon?.value || false,
          };
        }
        return buttonItem;
      });
    }
    return item.buttons || [];
  };

  // 获取车型类型数据（第一级）
  const getCarTypes = () => {
    if (!carousel || !Array.isArray(carousel)) return [];

    return carousel.map((item, index) => {
      return {
        id: item.id,
        title: item.fields?.title?.value || 'Brand Title', // Use item.fields.title for the brand name
        index: index,
        sitecoreItem: item, // Store the entire Sitecore item for later access
        timestamp: new Date().getTime(),
      };
    });
  };

  // 根据选中的车型类型获取车型数据（第二级）
  const getCarModels = () => {
    if (
      !selectedCarType ||
      !selectedCarType.sitecoreItem ||
      !selectedCarType.sitecoreItem.fields?.carType
    ) {
      return [];
    }

    const carModelsArray = selectedCarType.sitecoreItem.fields.carType; // Access the carType array directly

    return carModelsArray.map((carModel, index) => {
      return {
        id: carModel.id,
        title: carModel.fields?.title?.value || 'Car Model',
        images: getImages(carModel),
        description: getDescription(carModel),
        buttons: getButtons(carModel),
        index: index,
      };
    });
  };

  // 使用useMemo来缓存carTypes和carModels，确保它们只在依赖项变化时重新计�?
  const carTypes = React.useMemo(() => getCarTypes(), [carousel]);
  const carModels = React.useMemo(() => getCarModels(), [selectedCarType]);

  React.useEffect(() => {
    if (carModels.length > 0) {
      setSlideIndex(carModels[0]);
    } else {
      setSlideIndex(null);
    }
  }, [carModels]);

  // 如果没有选中车型类型，默认选择第一�?
  React.useEffect(() => {
    if (carTypes.length > 0) {
      // 检查是否需要更新：如果selectedCarType为空，或者carTypes timestamp 与当前选中的不同
      const shouldUpdate =
        !selectedCarType || selectedCarType?.timestamp !== carTypes[0]?.timestamp;
      if (shouldUpdate) {
        setSelectedCarType({ ...carTypes[0] });
      }
    }
  }, [carTypes, selectedCarType]);

  // 初始化完成后设置标志
  React.useEffect(() => {
    if (carTypes.length > 0 && !isInitialized) {
      setIsInitialized(true);
    }
  }, [carTypes, isInitialized]);

  const [slideIndex, setSlideIndex] = useState(null);

  // 当车型数据变化时更新slideIndex
  React.useEffect(() => {
    if (carModels.length > 0) {
      setSlideIndex(carModels[0]);
    } else {
      setSlideIndex(null);
    }
  }, [carModels]);

  // 处理车型类型选择
  const handleCarTypeSelect = (carTypeIndex) => {
    // 根据索引找到对应的完整carType对象
    const selectedCarTypeItem = carTypes.find((item) => item.index === carTypeIndex);

    if (selectedCarTypeItem) {
      setSelectedCarType({ ...selectedCarTypeItem });
    } else {
    }
  };

  // 安全检查：如果carousel为空，返回空组件
  if (!carousel || carousel.length === 0) {
    return null;
  }

  const handleClick = (idx) => {
    swiperRef.current?.slideTo(idx);
    const newData = carModels.find((el) => el.index === idx) ?? slideIndex;
    setSlideIndex(newData);
  };

  const segmentedData = carModels.map((item, index) => {
    // segmentedData now refers to carModels
    return {
      title: item.title,
      index: index,
    };
  });

  return (
    <div className="container">
      <div className={styles.features}>
        {/* 一级切换栏 - 车型类型选择 */}
        <div className={styles.features_segmented} data-aos="fade-up">
          <Segmented
            items={carTypes}
            activeIndex={selectedCarType?.index || 0}
            onClick={handleCarTypeSelect}
          />
        </div>
        {/* 二级切换�?- 具体车型选择 */}
        <div className={styles.switch} data-aos="fade-up">
          {segmentedData.map((item) => (
            <div
              key={item.index} // Use item.index for key since segmentedData is mapped from carModels
              role="switchpresentation"
              className={`${styles.switch_item} ${
                item.index === (slideIndex?.index || 0) ? styles.switch_active : ''
              }`}
              onClick={() => handleClick(item.index)}
            >
              {item.title}
            </div>
          ))}
        </div>
        <CarModelCarousel
          swiperRef={swiperRef}
          newCarouselData={carModels} // Pass carModels to carousel
          setSlideIndex={handleClick}
          buttonConfig={buttonConfig}
        />
      </div>
    </div>
  );
}
